/*
 * A channel's owner groups as the client reads them: which groups may own one, what their names
 * are, and what the channel is called because of them. One home for New channel, the About column's
 * Groups section and the administrator's Browse, so the three cannot say different things about the
 * same channel.
 *
 * `ConversationView.ownerGroupIds` holds the live owners only — the server leaves a deleted group
 * out — but the reader's model may still not know a group (it arrived a moment later, or the reader
 * lost sight of it), so every name here is drawn from the model and an id it does not know is left
 * out rather than shown as a number.
 *
 * The write that changes the owners lives with the other conversation writes in `manage.ts`.
 */

import { byGroupName, groupName, isPersonalGroup } from "../../../core/groups/group.ts"
import { formatList } from "../../../core/i18n/format.ts"
import { isAdmin } from "../../../core/people/standing.ts"
import type { GroupId, GroupView, Model } from "../../../core/store/model.ts"
import { t } from "../i18n/t.ts"

/**
 * Every group that may own a channel, in the directory's order (the specials first, widest first).
 * Never a personal group: a person who should read a channel is added to it, and the server refuses
 * a personal owner (`error.chat.ownerPersonal`).
 */
export function ownerCandidates(groups: Readonly<Record<GroupId, GroupView>>): GroupView[] {
	return Object.values(groups)
		.filter((group) => !isPersonalGroup(group))
		.sort(byGroupName)
}

/** The names of the owner groups the model knows, in the order the ids come; unknown ids are left out. */
export function ownerNames(
	ids: readonly GroupId[],
	groups: Readonly<Record<GroupId, GroupView>>,
): string[] {
	return ids.flatMap((id) => {
		const group = groups[id]
		return group === undefined ? [] : [groupName(group)]
	})
}

/**
 * What a room is called by its kind: "Channel for Team members", "Channel for its members" when no
 * owner group the reader knows is left, or "Private channel".
 */
export function channelKindLabel(
	kind: "channel" | "private",
	ownerGroupIds: readonly GroupId[],
	groups: Readonly<Record<GroupId, GroupView>>,
): string {
	if (kind === "private") return t("chat.header.kindPrivate")
	const names = ownerNames(ownerGroupIds, groups)
	return names.length === 0
		? t("chat.header.kindChannelMembers")
		: t("chat.header.kindChannelFor", { groups: formatList(names) })
}

/**
 * Whether a reader who leaves this room can find it again by themself: a channel one of whose live
 * owner groups holds them (Browse lists it), or any channel for an administrator (Other channels).
 * Anybody else — a guest added to a team channel, a team member added to a channel of a group they
 * are not in, every private room — needs somebody inside to add them again, and the Leave dialog
 * says which.
 */
export function rejoinableAfterLeaving(
	conv: { kind: string; ownerGroupIds: readonly GroupId[] },
	groups: Readonly<Record<GroupId, GroupView>>,
	me: Model["me"],
): boolean {
	if (conv.kind !== "channel" || me === null) return false
	if (isAdmin(me)) return true
	return conv.ownerGroupIds.some((id) => groups[id]?.memberIds.includes(me.id) === true)
}
